/**
 * HeaderActions — the page header's right-hand cluster of PAGE actions,
 * anchored top-right at every width.
 *
 * Scope: page links only (Back, the job in the Company's rental system).
 * Global destinations live in the persistent `AppNav` bar (see AppNav.tsx).
 *
 * Layout: inline row from `sm` up, where there is width for it; below `sm` the
 * actions collapse behind a kebab that stacks them full-width.
 *
 * A single action never collapses: a kebab holding one button is two taps and
 * a guess where there was room for the button itself. The kebab earns its
 * place only once it is hiding a list.
 *
 * DESIGN.md: actions are the shared `Button` component (no hover visual change);
 * the dropdown is just those buttons stacked — no bespoke menu-item styling.
 */
import { useRef, useState, type ReactNode } from "react";
import { Button } from "./Button";
import { MoreVerticalIcon, XIcon } from "./icons";
import { useDismissable } from "../../lib/use-dismissable";
import styles from "./HeaderActions.module.css";

export type HeaderAction =
  | { kind: "link"; label: string; to: string }
  | {
      kind: "external-link";
      label: string;
      href: string;
      target?: string;
      rel?: string;
    };

function renderAction(action: HeaderAction, fullWidth: boolean): ReactNode {
  const cls = fullWidth ? styles.actionFullWidth : styles.action;
  if (action.kind === "link") {
    // Navigation unmounts this header, so the menu closes on its own.
    return (
      <Button
        key={action.label}
        to={action.to}
        variant="secondary"
        size="sm"
        className={cls}
      >
        {action.label}
      </Button>
    );
  }
  return (
    <Button
      key={action.label}
      href={action.href}
      target={action.target}
      rel={action.rel}
      variant="secondary"
      size="sm"
      className={cls}
    >
      {action.label}
    </Button>
  );
}

export function HeaderActions({ actions }: { actions: HeaderAction[] }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // One action is its own affordance at every width; only a list collapses.
  const collapses = actions.length > 1;

  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    pointer: [menuRef],
  });

  return (
    <div className={styles.cluster}>
      {actions.length > 0 && (
        <>
          {/* The actions themselves, in a row: from `sm` when they collapse,
              at every width when there is only one. */}
          <div className={collapses ? styles.actionsCollapsing : styles.actions}>
            {actions.map((a) => renderAction(a, false))}
          </div>

          {/* Mobile: kebab → the same actions stacked full-width. */}
          {collapses && (
            <div ref={menuRef} className={styles.kebabWrap}>
              <button
                type="button"
                aria-label={open ? "Close menu" : "Open menu"}
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((o) => !o)}
                className={styles.kebab}
              >
                {open ? (
                  <XIcon className={styles.kebabIcon} />
                ) : (
                  <MoreVerticalIcon className={styles.kebabIcon} />
                )}
              </button>
              {open && (
                <div role="menu" className={styles.menu}>
                  {actions.map((a) => renderAction(a, true))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
